import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Lead } from '@/models/lead.model';
import { Account } from '@/models/account.model';
import { Contact } from '@/models/contact.model';
import { Opportunity } from '@/models/opportunity.model';

export class SalesService {
  // ==================== LEADS ====================

  static async getLeads(assignedTo?: string): Promise<Lead[]> {
    let q = query(
      collection(db, 'leads'),
      orderBy('createdAt', 'desc')
    );

    if (assignedTo) {
      q = query(q, where('assignedTo', '==', assignedTo));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Lead[];
  }

  static async getLead(id: string): Promise<Lead> {
    const docRef = doc(db, 'leads', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Lead not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Lead;
  }

  static async createLead(lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    console.log('[SalesService] Creating lead:', lead);
    
    try {
      // Remove undefined values - Firestore doesn't allow undefined
      const cleanedLead: any = {};
      Object.keys(lead).forEach((key) => {
        const value = (lead as any)[key];
        if (value !== undefined) {
          cleanedLead[key] = value;
        }
      });
      
      const leadData = {
        ...cleanedLead,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      console.log('[SalesService] Adding lead to Firestore:', leadData);
      const docRef = await addDoc(collection(db, 'leads'), leadData);
      console.log('[SalesService] Lead created successfully:', { leadId: docRef.id });
      
      return docRef.id;
    } catch (error: any) {
      console.error('[SalesService] Error creating lead:', {
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        stack: error?.stack,
        lead,
      });
      throw error;
    }
  }

  static async updateLead(id: string, updates: Partial<Lead>): Promise<void> {
    console.log('[SalesService] Updating lead:', { id, updates });
    
    try {
      // Remove undefined values - Firestore doesn't allow undefined
      const cleanedUpdates: any = {};
      Object.keys(updates).forEach((key) => {
        const value = (updates as any)[key];
        if (value !== undefined) {
          cleanedUpdates[key] = value;
        }
      });
      
      const docRef = doc(db, 'leads', id);
      const updateData = {
        ...cleanedUpdates,
        updatedAt: Timestamp.now(),
      };
      
      console.log('[SalesService] Updating lead document:', { id, updateData });
      await updateDoc(docRef, updateData);
      console.log('[SalesService] Lead updated successfully:', { id });
    } catch (error: any) {
      console.error('[SalesService] Error updating lead:', {
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        stack: error?.stack,
        id,
        updates,
      });
      throw error;
    }
  }

  static async deleteLead(id: string): Promise<void> {
    await deleteDoc(doc(db, 'leads', id));
  }

  // ==================== ACCOUNTS ====================

  static async getAccounts(assignedTo?: string): Promise<Account[]> {
    let q = query(
      collection(db, 'accounts'),
      orderBy('createdAt', 'desc')
    );

    if (assignedTo) {
      q = query(q, where('assignedTo', '==', assignedTo));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Account[];
  }

  static async getAccount(id: string): Promise<Account> {
    const docRef = doc(db, 'accounts', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Account not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Account;
  }

  static async createAccount(account: Omit<Account, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedAccount: any = {};
    Object.keys(account).forEach((key) => {
      const value = (account as any)[key];
      if (value !== undefined) {
        cleanedAccount[key] = value;
      }
    });
    
    const docRef = await addDoc(collection(db, 'accounts'), {
      ...cleanedAccount,
      contacts: cleanedAccount.contacts || [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  }

  static async updateAccount(id: string, updates: Partial<Account>): Promise<void> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedUpdates: any = {};
    Object.keys(updates).forEach((key) => {
      const value = (updates as any)[key];
      if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    });
    
    const docRef = doc(db, 'accounts', id);
    await updateDoc(docRef, {
      ...cleanedUpdates,
      updatedAt: Timestamp.now(),
    });
  }

  static async deleteAccount(id: string): Promise<void> {
    await deleteDoc(doc(db, 'accounts', id));
  }

  // ==================== CONTACTS ====================

  static async getContacts(accountId?: string): Promise<Contact[]> {
    let q = query(
      collection(db, 'contacts'),
      orderBy('createdAt', 'desc')
    );

    if (accountId) {
      q = query(q, where('accountId', '==', accountId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Contact[];
  }

  static async getContact(id: string): Promise<Contact> {
    const docRef = doc(db, 'contacts', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Contact not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Contact;
  }

  static async createContact(contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedContact: any = {};
    Object.keys(contact).forEach((key) => {
      const value = (contact as any)[key];
      if (value !== undefined) {
        cleanedContact[key] = value;
      }
    });
    
    const docRef = await addDoc(collection(db, 'contacts'), {
      ...cleanedContact,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  }

  static async updateContact(id: string, updates: Partial<Contact>): Promise<void> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedUpdates: any = {};
    Object.keys(updates).forEach((key) => {
      const value = (updates as any)[key];
      if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    });
    
    const docRef = doc(db, 'contacts', id);
    await updateDoc(docRef, {
      ...cleanedUpdates,
      updatedAt: Timestamp.now(),
    });
  }

  static async deleteContact(id: string): Promise<void> {
    await deleteDoc(doc(db, 'contacts', id));
  }

  // ==================== OPPORTUNITIES ====================

  static async getOpportunities(accountId?: string, assignedTo?: string): Promise<Opportunity[]> {
    let q = query(
      collection(db, 'opportunities'),
      orderBy('expectedCloseDate', 'desc')
    );

    if (accountId) {
      q = query(q, where('accountId', '==', accountId));
    }

    if (assignedTo) {
      q = query(q, where('assignedTo', '==', assignedTo));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      expectedCloseDate: doc.data().expectedCloseDate?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Opportunity[];
  }

  static async getOpportunity(id: string): Promise<Opportunity> {
    const docRef = doc(db, 'opportunities', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Opportunity not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      expectedCloseDate: data.expectedCloseDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Opportunity;
  }

  static async createOpportunity(opportunity: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedOpportunity: any = {};
    Object.keys(opportunity).forEach((key) => {
      const value = (opportunity as any)[key];
      if (value !== undefined && key !== 'expectedCloseDate') {
        cleanedOpportunity[key] = value;
      }
    });
    
    const docRef = await addDoc(collection(db, 'opportunities'), {
      ...cleanedOpportunity,
      expectedCloseDate: Timestamp.fromDate(opportunity.expectedCloseDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  }

  static async updateOpportunity(id: string, updates: Partial<Opportunity>): Promise<void> {
    // Remove undefined values - Firestore doesn't allow undefined
    const cleanedUpdates: any = {};
    Object.keys(updates).forEach((key) => {
      const value = (updates as any)[key];
      if (value !== undefined && key !== 'expectedCloseDate') {
        cleanedUpdates[key] = value;
      }
    });
    
    const docRef = doc(db, 'opportunities', id);
    const updateData: any = {
      ...cleanedUpdates,
      updatedAt: Timestamp.now(),
    };

    if (updates.expectedCloseDate) {
      updateData.expectedCloseDate = Timestamp.fromDate(updates.expectedCloseDate);
    }

    await updateDoc(docRef, updateData);
  }

  static async deleteOpportunity(id: string): Promise<void> {
    await deleteDoc(doc(db, 'opportunities', id));
  }
}

